/* oxlint-disable max-lines-per-function */
import { getProjectProgressStatus, getStreamProgressStatus, useContentProgress } from 'ContentProgress';
import { usePlaylist } from 'Playlists';
import { useRequiredCurrentUser } from 'FrontRoyalAngular';
import { type StreamForDashboard } from 'Lessons';
import { useStudentDashboardStreams } from 'Streams';
import { useMemo, useCallback } from 'react';
import { getActivePlaylistLocalePackId } from 'Users';
import { type LearnerProject, useProjectsForPlaylist } from 'LearnerProjects';

export function useGetActivePlaylistStreams() {
    const currentUser = useRequiredCurrentUser();
    const { streamProgressByLocalePackId, lessonProgressByLocalePackId, projectProgress } = useContentProgress();
    const activePlaylistLocalePackId = getActivePlaylistLocalePackId(currentUser) ?? '';
    const activePlaylist = usePlaylist(activePlaylistLocalePackId);

    const activePlaylistStreamLocalePackIds = useMemo(
        () => activePlaylist?.streamEntries.map(entry => entry.localePackId) ?? [],
        [activePlaylist],
    );
    const activePlaylistStreams = useStudentDashboardStreams(activePlaylistStreamLocalePackIds);
    const projects = useProjectsForPlaylist(activePlaylistLocalePackId);

    const getProgressInfo = useCallback(
        ({
            type,
            content,
        }: { type: 'stream'; content: StreamForDashboard } | { type: 'project'; content: LearnerProject }) => {
            if (type === 'stream') {
                const streamProgress = streamProgressByLocalePackId[content.localePackId];
                const lessonsProgressForStream = content.lessons
                    .map(l => lessonProgressByLocalePackId[l.localePackId] || null)
                    .filter(p => !!p);
                const totalLessons = content.lessons?.length ?? undefined;
                const completedLessons = lessonsProgressForStream.filter(p => p?.complete).length;

                return {
                    status: getStreamProgressStatus(streamProgress),
                    totalLessons,
                    completedLessons,
                    hasStarted: !!lessonsProgressForStream.length,
                };
            }

            const progress = projectProgress.find(p => p.requirementIdentifier === content.requirementIdentifier);
            return {
                status: getProjectProgressStatus(progress),
            };
        },
        [streamProgressByLocalePackId, lessonProgressByLocalePackId, projectProgress],
    );

    const numCourses = activePlaylistStreams.filter(stream => stream.exam === false).length;
    const numExams = activePlaylistStreams.filter(stream => stream.exam === true).length;
    const numCompleteStreams = activePlaylistStreams.filter(
        stream => getProgressInfo({ type: 'stream', content: stream })?.status === 'completed',
    ).length;
    const numCompleteProjects = projects.filter(
        project => getProgressInfo({ type: 'project', content: project })?.status === 'completed',
    ).length;
    const numComplete = numCompleteStreams + numCompleteProjects;

    return {
        activePlaylistStreams,
        activePlaylist,
        numCourses,
        numExams,
        numComplete,
        projects,
        getProgressInfo,
    };
}
